import { useEffect } from 'react';

/**
 * Нэгж бараа үүсгэх урсгалын хагас дутуу мэдээллийг (draft) хадгалах.
 *
 * Бүх төлөв нь React context-д байдаг тул хуудсаа refresh хийхэд бөглөсөн
 * мэдээлэл алга болдог байсан. sessionStorage-д хадгалснаар refresh хийсэн ч
 * бөглөсөн талбар, оруулсан зураг, хувилбарууд сэргэнэ.
 *
 * Аль алхам дээр байгааг нь URL-ийн `?step=` параметр агуулна (ProductStep.js)
 * — ингэснээр refresh, browser-ийн back/forward хоёулаа ажиллана.
 *
 * ХАМРАХ ХҮРЭЭ: draft нь ЗӨВХӨН тухайн хуудас дээр байх хугацаанд л хүчинтэй.
 * Өөр хуудас руу орвол `useClearDraftOnLeave` шууд цэвэрлэдэг тул дараа дахин
 * орж ирэхэд форм хоосон эхэлнэ (доорх hook-ийг үзнэ үү).
 *
 * sessionStorage-ыг сонгосон шалтгаан: таб хаагдахад өөрөө цэвэрлэгдэнэ,
 * өөр таб дээрх шинэ бараанд хөндлөнгөөс оролцохгүй.
 *
 * `ProductProvider`-т key дамжуулаагүй бол draft бүрэн унтарна.
 *
 * Хүргэлт (views/orderDelivery/Add/draft.js), буцаалт (views/orderReturn/Add/draft.js),
 * татан авалт (views/orders/draft.js) дээр ижил механизм байгаа — зан төлөвийг
 * өөрчлөх бол бүгдийг харна.
 */
export const PRODUCT_DRAFT_KEY = 'add_product_draft';

/**
 * Draft-ын key-г merchant тус бүрд салгана.
 *
 * `changeMerchant` (store/createUserSlice.js) нь merchant солиход
 * `window.location.href`-ээр НЭГ ЛЭ таб дотор дахин уншуулдаг тул sessionStorage
 * хэвээрээ үлддэг. Key нь тогтмол байсан бол өмнөх merchant дээр бөглөсөн
 * мэдээлэл (жиш нь тухайн merchant-д байхгүй категори) шинэ merchant-ийн формд
 * сэргэх байсан.
 */
export const productDraftKey = (merchantCode) =>
  merchantCode ? `${PRODUCT_DRAFT_KEY}_${merchantCode}` : PRODUCT_DRAFT_KEY;

export const readDraft = (key) => {
  if (!key) return {};

  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error('readDraft error: ', error);
    return {};
  }
};

export const writeDraft = (key, draft) => {
  if (!key) return;

  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    // Хэмжээ хэтэрсэн зэрэг тохиолдолд урсгалыг тасалдуулахгүй
    console.error('writeDraft error: ', error);
  }
};

/** Бараа амжилттай үүссэний дараа дуудаж draft-ыг цэвэрлэнэ */
export const clearDraft = (key) => {
  if (!key) return;

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('clearDraft error: ', error);
  }
};

/**
 * Хуудсаас гармагц draft-ыг цэвэрлэнэ.
 *
 * Бараа үүсгэх хуудас unmount болох (өөр хуудас руу орох, толгойн "back",
 * breadcrumb, хажуугийн меню, browser-ийн back — бүгд) үед л cleanup ажиллана.
 * Тиймээс хагас бөглөсөн мэдээлэл дараа хуудсанд дахин орж ирэхэд сэргэхгүй.
 *
 * Refresh дээр ХАМААРАХГҮЙ: хуудас дахин уншихад JS бүхэлдээ унтардаг тул
 * React cleanup ажиллаж чаддаггүй — refresh-ийн сэргээлт хэвээрээ.
 *
 * Алхам солих (`?step=`) нь зөвхөн search-ийг өөрчилдөг, route нэг хэвээрээ
 * тул unmount болохгүй — алхам хооронд draft хадгалагдана.
 *
 * `key` нь merchant-аар өөрчлөгддөг (`productDraftKey`) тул merchant солиход
 * өмнөх merchant-ийн draft мөн адил цэвэрлэгдэнэ.
 */
export const useClearDraftOnLeave = (key) => {
  useEffect(() => () => clearDraft(key), [key]);
};
